if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        /*
          NOTE (Pause Rituals): Dawn wrapped this in debounce(…, 500).
          Two problems on a mobile swipe:
            1. the thumbnail ring lagged half a second behind the image;
            2. onSlideChanged only moved aria-current, so `.is-active` stayed on the
               slide the user had swiped AWAY from. Everything else that reads the
               active slide — the hover-zoom target, the lightbox, playActiveMedia,
               and the thumbnail sync in pdp-enhance.js — kept pointing at the wrong
               media, and the next re-sync snapped the ring back to the stale slide.
          It now routes through setActiveMedia, so `.is-active` is the single source
          of truth, and it is no longer debounced so the ring tracks the swipe live.
          The intermediate-slide flicker the debounce used to mask is handled by the
          programmatic-scroll guard below instead.
        */
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          // An arrow function rather than .bind(): bind would hand the click event
          // through as the third argument, where the options object now lives.
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        // Ignore the scroll we started ourselves: jumping from slide 1 to slide 4
        // passes over 2 and 3, and each would otherwise flash as the active image.
        if (this.programmaticScroll) return;

        const mediaId = event.detail?.currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        // scrollViewer: false — the user is already scrolling it; scrolling it again
        // from here would fight the swipe.
        this.setActiveMedia(mediaId, false, { scrollViewer: false });
      }

      /*
        Slide-level nodes only, and never a descendant query.
        snippets/product-thumbnail.liquid stamps a bare data-media-id="{media}" on the
        inner media node as well as the prefixed one on the <li>. An unscoped
        querySelector can match an inner node, and `.is-active` then lands on it rather
        than on the slide — leaving no active <li>, which the thumbnail layout renders
        as an empty gallery.
      */
      mediaItems() {
        const scope = this.elements.viewer.querySelector('.product__media-list') || this.elements.viewer;
        return Array.from(scope.children).filter((element) => element.hasAttribute('data-media-id'));
      }

      setActiveMedia(mediaId, prepend, options) {
        const scrollViewer = !options || options.scrollViewer !== false;

        const items = this.mediaItems();
        // Matching on the property rather than a selector also avoids having to escape
        // whatever characters end up in a media id.
        const activeMedia = items.find((element) => element.dataset.mediaId === mediaId) || items[0];
        if (!activeMedia) {
          return;
        }
        items.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (scrollViewer) {
          this.preventStickyHeader();
          this.beginProgrammaticScroll();
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              // Horizontal-only: aligns the active slide within the mobile carousel rail.
              // scrollTo with just `left` leaves the rail's vertical position untouched.
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            // NOTE (Pause Rituals): Dawn called window.scrollTo() here to pull the newly
            // active media into view, which yanked the whole PAGE upward when selecting a
            // thumbnail or changing a variant after the user had scrolled down. Removed so
            // changing media NEVER moves the page scroll — the user stays exactly where
            // they are; only the main image swaps.
          });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      /*
        Marks the viewer as "we are driving" until its scroll settles, so the
        slideChanged events thrown off by our own animation are ignored.
        scrollend where the browser has it, a timeout everywhere else (and as a
        backstop for the case where the scroll position never actually changes —
        on desktop every inactive slide is display:none, so offsetLeft is always 0
        and no scroll event ever fires).
      */
      beginProgrammaticScroll() {
        this.programmaticScroll = true;
        window.clearTimeout(this.programmaticScrollTimer);

        const release = () => {
          this.programmaticScroll = false;
          window.clearTimeout(this.programmaticScrollTimer);
        };

        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        if (slider && 'onscrollend' in window) {
          slider.addEventListener('scrollend', release, { once: true });
        }
        this.programmaticScrollTimer = window.setTimeout(release, 700);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
